import { Link } from "react-router-dom";
import {
    MarkSealM,
    MarkCircleSeal,
    MarkChevronM,
    MarkMonogram,
    MarkPaxCross,
    WordmarkDot,
} from "./MedpaxLogo";

const options = [
    {
        key: "seal-m",
        name: "Option 1 · Seal M",
        description:
            "Architectural serif M inside a square seal. Trustworthy, corporate, quietly classic.",
        Mark: MarkSealM,
    },
    {
        key: "circle-seal",
        name: "Option 2 · Circle Seal",
        description:
            "Concentric-circle seal with compass-point dots — notarial, old-money, authoritative.",
        Mark: MarkCircleSeal,
    },
    {
        key: "chevron-m",
        name: "Option 3 · Chevron M",
        description:
            "Bold, modern M drawn from two overlapping chevrons. Technology-forward, confident.",
        Mark: MarkChevronM,
    },
    {
        key: "monogram",
        name: "Option 4 · Editorial Monogram",
        description:
            "Refined M with serif feet and a swash, framed by thin editorial rules. Most elegant.",
        Mark: MarkMonogram,
    },
    {
        key: "pax-cross",
        name: "Option 5 · Pax Cross",
        description:
            "Minimal M with a small cross tucked into the valley — quietly references 'pax' (peace).",
        Mark: MarkPaxCross,
    },
];

export const LogosPreview = () => {
    return (
        <main className="min-h-screen bg-white text-[var(--ink)] pb-32">
            <header className="border-b border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <Link
                        to="/"
                        className="font-display text-xl text-[var(--ink)]"
                        data-testid="logos-back-home"
                    >
                        ← Medpax
                    </Link>
                    <span className="overline text-[var(--slate)]">Logo concepts</span>
                </div>
            </header>

            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
                <p className="overline text-[var(--slate)]">
                    Internal preview · not publicly linked
                </p>
                <h1 className="font-display text-5xl md:text-6xl mt-6 leading-[1.02]">
                    Six directions for the
                    <span className="italic text-[var(--ink-line)]"> Medpax </span>
                    mark.
                </h1>
                <p className="mt-6 max-w-2xl text-[var(--slate)] text-lg leading-relaxed">
                    Each concept is a pure-vector SVG so it scales perfectly from
                    a 16px favicon to a printed letterhead. Below each option you
                    can see the mark at three sizes, on white and on deep navy,
                    and paired with the Medpax wordmark. Reply with the option
                    number you'd like to keep.
                </p>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
                {options.map(({ key, name, description, Mark }) => (
                    <article
                        key={key}
                        data-testid={`logo-option-${key}`}
                        className="border border-[var(--line)] overflow-hidden"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-12">
                            {/* Description */}
                            <div className="lg:col-span-4 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-[var(--line)]">
                                <p className="overline text-[var(--slate)]">
                                    {key.toUpperCase()}
                                </p>
                                <h2 className="font-display text-3xl mt-3">
                                    {name}
                                </h2>
                                <p className="mt-4 text-[var(--slate)] leading-relaxed">
                                    {description}
                                </p>
                            </div>

                            {/* White variant */}
                            <div className="lg:col-span-4 p-10 flex flex-col items-center justify-center gap-6 bg-white border-b lg:border-b-0 lg:border-r border-[var(--line)]">
                                <Mark size={120} color="#0A192F" />
                                <div className="flex items-end gap-6">
                                    <Mark size={48} color="#0A192F" />
                                    <Mark size={24} color="#0A192F" />
                                </div>
                                <div className="flex items-center gap-3 pt-3 border-t border-[var(--line)] w-full justify-center">
                                    <Mark size={24} color="#0A192F" />
                                    <span className="font-display text-xl text-[var(--ink)]">
                                        Medpax, LLC
                                    </span>
                                </div>
                            </div>

                            {/* Navy variant */}
                            <div className="lg:col-span-4 p-10 flex flex-col items-center justify-center gap-6 bg-[var(--ink)] text-white">
                                <Mark size={120} color="#ffffff" />
                                <div className="flex items-end gap-6">
                                    <Mark size={48} color="#ffffff" />
                                    <Mark size={24} color="#ffffff" />
                                </div>
                                <div className="flex items-center gap-3 pt-3 border-t border-white/20 w-full justify-center">
                                    <Mark size={24} color="#ffffff" />
                                    <span className="font-display text-xl text-white">
                                        Medpax, LLC
                                    </span>
                                </div>
                            </div>
                        </div>
                    </article>
                ))}

                {/* Wordmark-only option */}
                <article
                    data-testid="logo-option-wordmark"
                    className="border border-[var(--line)] overflow-hidden"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-12">
                        <div className="lg:col-span-4 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-[var(--line)]">
                            <p className="overline text-[var(--slate)]">WORDMARK</p>
                            <h2 className="font-display text-3xl mt-3">
                                Option 6 · Pure Wordmark
                            </h2>
                            <p className="mt-4 text-[var(--slate)] leading-relaxed">
                                No symbol — just "Medpax" set in Cormorant Garamond
                                with a single dot accent. Most minimal and
                                editorial; lets the typography carry the brand.
                            </p>
                        </div>
                        <div className="lg:col-span-4 p-10 flex flex-col items-center justify-center gap-8 bg-white border-b lg:border-b-0 lg:border-r border-[var(--line)]">
                            <WordmarkDot size={56} color="#0A192F" />
                            <WordmarkDot size={28} color="#0A192F" />
                        </div>
                        <div className="lg:col-span-4 p-10 flex flex-col items-center justify-center gap-8 bg-[var(--ink)]">
                            <WordmarkDot size={56} color="#ffffff" />
                            <WordmarkDot size={28} color="#ffffff" />
                        </div>
                    </div>
                </article>
            </section>

            <p className="mt-16 text-center text-sm text-[var(--slate)]">
                Once you pick one, I'll swap it across the nav, footer and favicon.
            </p>
        </main>
    );
};

export default LogosPreview;
